import { useCallback, useEffect, useEffectEvent, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  connectProvider as apiConnect,
  disconnectProvider as apiDisconnect,
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  ProviderErrorResponse,
  UnauthorizedError,
  type AuthUser,
  type LoginOutcome,
} from '@apis/auth';
import type { IConnectProviderOptions, ProviderName } from '@interfaces/IAuth';
import type { UnifiedProject } from '@interfaces/IUnifiedProject';
import { store } from '@store/store';
import { clearWorkingSite } from '@store/slices/site.slice';
import { clearProviderIssue } from '@store/slices/providerStatus.slice';
import { PROJECTS_LIST_KEY, PORTFOLIO_KEY } from '@constants/queryKeys';
import { AuthContext, type AuthContextValue } from './authContext.internal';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthContextValue['user']>(null);
  // Auth is cookie-based, so there's nothing in JS to inspect at mount — the
  // only way to know if a session exists is to ask the server. Always start
  // hydrating and resolve via GET /api/auth/me.
  const [isHydrating, setIsHydrating] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    const ctrl = new AbortController();
    fetchMe(ctrl.signal)
      .then((u) => {
        if (!ctrl.signal.aborted) setUser(u);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof UnauthorizedError) {
          // No active session — the guarded routes will bounce to /login.
          setUser(null);
        } else {
          // Backend unreachable / 5xx during bootstrap. Make it visible instead
          // of silently landing the user on /login with no explanation.
          console.error('auth bootstrap failed:', err);
        }
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setIsHydrating(false);
      });
    return () => ctrl.abort();
  }, []);

  // authedFetch dispatches `dorca:auth-expired` after a refresh attempt fails.
  // Clearing `user` flips ProtectedRoute → <Navigate to="/login" />, and
  // `queryClient.clear()` drops any pending/error queries that would otherwise
  // flash a red banner during the redirect.
  const onAuthExpired = useEffectEvent(() => {
    setUser(null);
    queryClient.clear();
  });
  useEffect(() => {
    window.addEventListener('dorca:auth-expired', onAuthExpired);
    return () => window.removeEventListener('dorca:auth-expired', onAuthExpired);
  }, []); // onAuthExpired is stable via useEffectEvent; subscribe once for the lifetime

  const refreshUser = useCallback(async () => {
    try {
      const u = await fetchMe();
      setUser(u);
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setUser(null);
      } else {
        throw err;
      }
    }
  }, []);

  /**
   * Everything that has to happen once a real session exists, regardless of
   * whether it came from a password-only login or from completing an MFA
   * challenge. Invalidate first to drop any stale per-user data from a prior
   * session, then seed the projects cache with the list embedded in the auth
   * response (when the backend piggybacks it). setQueryData wins over the
   * invalidate for this key, so the dropdown renders without a /api/projects
   * round-trip on first paint.
   */
  const adoptSession = useCallback(async (u: AuthUser, projects?: UnifiedProject[]) => {
    setUser(u);
    await queryClient.invalidateQueries();
    if (projects) {
      queryClient.setQueryData(PROJECTS_LIST_KEY, projects);
    }
  }, [queryClient]);

  const login = useCallback(async (email: string, password: string): Promise<LoginOutcome> => {
    const outcome = await apiLogin(email, password);
    // The MFA outcomes carry no session yet (`dorca_pending` only) — leave
    // `user` null so ProtectedRoute keeps the app shell out of reach until the
    // second factor lands.
    if (outcome.kind === 'authenticated') {
      await adoptSession(outcome.user, outcome.projects);
    }
    return outcome;
  }, [adoptSession]);

  const completeMfaLogin = useCallback(async (u: AuthUser, projects?: UnifiedProject[]) => {
    await adoptSession(u, projects);
  }, [adoptSession]);

  const setMfaEnabled = useCallback((enabled: boolean) => {
    setUser((prev) => (prev ? { ...prev, mfaEnabled: enabled } : prev));
  }, []);

  const logout = useCallback(() => {
    // Fire-and-forget the server logout (clears the session + cookie); the
    // client state is dropped immediately regardless of the round-trip.
    void apiLogout();
    setUser(null);
    queryClient.clear();
    store.dispatch(clearWorkingSite());
  }, [queryClient]);

  const connectProvider = useCallback(async (
    provider: ProviderName,
    username: string,
    password: string,
    opts?: IConnectProviderOptions,
  ) => {
    await apiConnect(provider, username, password, opts);
    // The provider is freshly connected — clear the global "needs reconnect"
    // flag (set by the TopBar provider-status poll) so the TopBar prompt
    // disappears immediately, then re-fetch the poll to confirm against the
    // backend (the single detection path) rather than waiting up to its interval.
    store.dispatch(clearProviderIssue(provider));
    //
    // What the user waits on (spinner + redirect) is ONLY this awaited block:
    // the cheap provider-status confirmation, the specific page the user
    // reconnected FROM (matched via the typed ProviderErrorResponse), and the
    // /me refresh — all in PARALLEL, so the cost is the slowest one, not the sum.
    // `refreshUser` is folded in here (rather than awaited serially in front) so
    // its round-trip overlaps the refetches instead of preceding them.
    //
    // NOTE on `refetchType: 'all'`: it does NOT mean "all queries"; the
    // predicate already selects WHICH queries match, and `refetchType` only
    // chooses whether to refetch the matched query's active (mounted) instances
    // or also its inactive (unmounted) ones. We need 'all' because the page the
    // user reconnected FROM is unmounted (we're on /connect), so the active-only
    // default would skip it and the redirect back would show stale data.
    await Promise.all([
      // /me — refresh the bound provider flags + catch a now-dead session.
      // Runs in parallel; a non-auth failure still rejects and surfaces.
      refreshUser(),
      // Re-confirm the provider-status poll so the TopBar reconnect button
      // clears immediately instead of lingering until the next 60s poll tick.
      queryClient.invalidateQueries({ queryKey: ['provider-status'] }),
      // The queries that broke BECAUSE of this provider — the typed
      // `ProviderErrorResponse` carries which provider failed, so reconnecting
      // Genba won't touch a page that's down because Trusstor dropped. This is
      // the page the user actually lands on, so it stays AWAITED (pre-warmed,
      // no error/loading flash on arrival).
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.state.error instanceof ProviderErrorResponse &&
          query.state.error.provider === provider,
        refetchType: 'all',
      }),
    ]);
    // Deferred to the BACKGROUND (not awaited) so the spinner clears and we
    // redirect without waiting on the heavy, page-agnostic refetches:
    //  - PROJECTS_LIST_KEY: the connected-provider set changes on connect, but
    //    it's stale-SUCCESS (not errored), so the predicate above misses it.
    //  - PORTFOLIO_KEY: a BFF aggregate (3 live Trusstor calls per synced site)
    //    that degrades GRACEFULLY, so its "data unavailable" strips come from a
    //    SUCCESSFUL response the error predicate can't see.
    // Both are stale-SUCCESS, so TanStack keeps the prior data visible while the
    // background refetch runs — the user sees a quiet content update on arrival,
    // never an empty/error flash. The `.catch` keeps the floating promise from
    // becoming an unhandled rejection if a background refetch fails.
    // `refetchType: 'active'` (not 'all'): PORTFOLIO_KEY is now a prefix over
    // per-schedule pinned keys (`portfolioKey(siteId, scheduleNumber)`) that,
    // with `gcTime: Infinity`, accumulate one entry per schedule switched to.
    // 'all' would fire the heavy BFF refetch once PER accumulated key; 'active'
    // refetches only the mounted portfolio and leaves the rest marked stale to
    // refetch lazily on next visit.
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: PROJECTS_LIST_KEY }),
      queryClient.invalidateQueries({ queryKey: PORTFOLIO_KEY, refetchType: 'active' }),
    ]).catch(() => {});
  }, [queryClient, refreshUser]);

  const disconnectProvider = useCallback(async (provider: ProviderName) => {
    await apiDisconnect(provider);
    await refreshUser();
    await queryClient.invalidateQueries({ queryKey: PROJECTS_LIST_KEY });
  }, [queryClient, refreshUser]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isHydrating,
      login,
      completeMfaLogin,
      setMfaEnabled,
      logout,
      connectProvider,
      disconnectProvider,
      refreshUser,
    }),
    [user, isHydrating, login, completeMfaLogin, setMfaEnabled, logout, connectProvider, disconnectProvider, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
